import type { VehicleType } from "@/lib/booking/search-params";
import {
  filterVehiclesByTab,
  getVehicleTypeName,
  type Vehicle,
} from "@/lib/vehicles";

export type VehicleFilters = {
  categoryIds: number[];
  fuelTypeIds: number[];
  transmissionIds: number[];
  minPassengers: number | null;
};

export const emptyVehicleFilters: VehicleFilters = {
  categoryIds: [],
  fuelTypeIds: [],
  transmissionIds: [],
  minPassengers: null,
};

export function hasActiveFilters(filters: VehicleFilters): boolean {
  return (
    filters.categoryIds.length > 0 ||
    filters.fuelTypeIds.length > 0 ||
    filters.transmissionIds.length > 0 ||
    filters.minPassengers !== null
  );
}

function vehicleMatchesCategory(vehicle: Vehicle, categoryId: number): boolean {
  const name = getVehicleTypeName(vehicle).toLowerCase();

  switch (categoryId) {
    case 1:
      return (
        /car|sedan|hatchback|swift|camry|vitz/i.test(name) &&
        !/suv|van|ute|truck|hiace|transit|sprinter/i.test(name)
      );
    case 2:
      return /suv|rav4|dualis|captiva|asx|outlander|kluger|pajero|zs|tiggo|eclipse|haval/i.test(
        name,
      );
    case 3:
      return (
        /van|carnival|commuter|elgrand|passenger/i.test(name) &&
        !/delivery|hiace|transit|sprinter|master|truck|ute/i.test(name)
      );
    case 4:
      return /delivery|hiace|transit|sprinter|master|1t |2t /i.test(name);
    case 5:
      return /ute|hilux/i.test(name);
    case 6:
      return /truck|isuzu|pallet/i.test(name);
    default:
      return false;
  }
}

export function applyVehicleFilters(
  vehicles: Vehicle[],
  filters: VehicleFilters,
  vehicleTab: VehicleType | null,
): Vehicle[] {
  let result = filterVehiclesByTab(vehicles, vehicleTab);

  if (filters.categoryIds.length > 0) {
    result = result.filter((vehicle) =>
      filters.categoryIds.some((categoryId) =>
        vehicleMatchesCategory(vehicle, categoryId),
      ),
    );
  }

  if (filters.fuelTypeIds.length > 0) {
    result = result.filter((vehicle) => {
      if (!vehicle.fuel_types) {
        return false;
      }

      return filters.fuelTypeIds.includes(Number(vehicle.fuel_types));
    });
  }

  if (filters.transmissionIds.length > 0) {
    result = result.filter((vehicle) => {
      if (!vehicle.transmission_types) {
        return false;
      }

      return filters.transmissionIds.includes(
        Number(vehicle.transmission_types),
      );
    });
  }

  if (filters.minPassengers !== null) {
    result = result.filter(
      (vehicle) => vehicle.no_of_passengers >= filters.minPassengers!,
    );
  }

  return result;
}
